import type {ChallengeResult, Sealed} from "../types";
import {doProofOfWork} from "./work";
import {debug, info, warn} from "../utils/log";
import {apiFetch} from "../utils/api";
import {API_PREFIX, ID_PREFIX} from "../utils/constants";
import {getSignKeyPublic, signDataInline} from "../utils/crypto";
import {TranslatableError} from "../utils/error";

const encodeHex = (arr: Uint8Array) => {
    let result = '';
    for (let i = 0; i < arr.length; i++) {
        const hex = arr[i].toString(16).padStart(2, '0');
        result += hex;
    }
    return result
}

export async function getNewSolution(update: (rate: number) => void, signal: AbortSignal): Promise<string | undefined> {
    const challenge = await apiFetch<ChallengeResult>(API_PREFIX + "pow/challenge", {
        body: {
            publicSignKey: await getSignKeyPublic(),
        },
        signal: signal,
    });
    if (challenge.rejected) {
        throw new TranslatableError({id: 'rejected'})
    }
    const startNonce = getStartNonce(challenge);
    const t0 = Date.now();
    let lastSpeedUpdate = t0;
    let lastIter = Number(startNonce);
    update(startNonce.rate)
    const {nonce, result} = await doProofOfWork(
        challenge,
        startNonce.nonce,
        (currentNonce) => {
            const currentNonceNumber = Number(currentNonce);
            const n = Date.now()
            const delta = n - lastSpeedUpdate;
            const rate = delta === 0 ? 0 : (currentNonceNumber - lastIter) / delta;
            update(rate)
            storeStartNonce(challenge, {nonce: currentNonce, rate});
            lastSpeedUpdate = n;
            lastIter = currentNonceNumber;
        },
        signal,
    );
    const t1 = Date.now();
    const elapsed = t1 - t0;
    cleanupNonceStorage()
    debug(`New solution computed: ${nonce} in ${elapsed}ms`, {challenge, nonce, result, elapsed, resultHex: encodeHex(result)});
    const response = await apiFetch<Sealed>(API_PREFIX + "pow/submit", {
        body: {
            challenge: challenge.challenge,
            nonce: await signDataInline(nonce.toString()),
            elapsed,
            publicSignKey: await getSignKeyPublic(),
        },
        signal: signal,
    });

    const solution = JSON.stringify(response.solution);
    info(`New sealed solution obtained: ${solution}`);
    return solution;
}


const NONCE_KEY_PREFIX = ID_PREFIX + "nonce||";

// const sealRefreshBefore = 1000 * 60 * 60 * 2; // 2 hours

function getNonceKey(challenge: ChallengeResult): string {
    return `${NONCE_KEY_PREFIX}${challenge.challenge}-${challenge.target}`;
}

const removeItem = localStorage.removeItem.bind(localStorage);

function cleanupNonceStorage(nonceKey?: string): void {
    let oldKeys: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(NONCE_KEY_PREFIX) && key !== nonceKey) {
            oldKeys.push(key);
        }
    }
    oldKeys.forEach(removeItem)
}

type StartNonce = {nonce: string; rate: number}

function getStartNonce(challenge: ChallengeResult): StartNonce {
    const nonceKey = getNonceKey(challenge);
    cleanupNonceStorage(nonceKey)
    try {
        const rawNonce = JSON.parse(localStorage.getItem(nonceKey) || "") as StartNonce;
        if (rawNonce && typeof rawNonce === 'object' && 'nonce' in rawNonce && 'rate' in rawNonce && BigInt(rawNonce.nonce) >= 0n && typeof rawNonce.rate === 'number') {
            return rawNonce
        } else {
            warn("Invalid start nonce data:", rawNonce);
        }
    } catch {}
    return {
        nonce: "0",
        rate: 0,
    }
}

function storeStartNonce(challenge: ChallengeResult, data: StartNonce) {
    const nonceKey = getNonceKey(challenge);
    try {
        localStorage.setItem(nonceKey, JSON.stringify(data));
    } catch (err) {
        console.warn("Failed to store start nonce:", err);
    }
}
